import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { smbsApi, SMBData } from '../services/api'

export interface SMBContextValue {
  smbs: SMBData[]
  selectedSMB: SMBData | null
  selectedSMBId: string
  loading: boolean
  isError: boolean
  setSelectedSMBId: (smbId: string) => void
  refreshSMBs: () => Promise<void>
}

const SMBContext = createContext<SMBContextValue | undefined>(undefined)

const SESSION_STORAGE_KEY = 'selected_smb_id'

export const SMBProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const [selectedId, setSelectedId] = useState<string>(() => {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) || ''
  })

  // TanStack Query for fetching SMB list
  const {
    data: smbsData,
    isLoading: isSmbsLoading,
    isError: isSmbsError,
  } = useQuery({
    queryKey: ['smbsList'],
    queryFn: () => smbsApi.getSMBs({ page: 1, limit: 100 }),
    enabled: Boolean(isAuthenticated),
  })

  const smbs = useMemo(() => smbsData?.smbs || [], [smbsData])

  // Derive active SMB from session storage ground truth and fetched list
  const selectedSMB = useMemo(() => {
    if (!isAuthenticated || smbs.length === 0) return null

    // Session storage is ground truth (cleared automatically on logout)
    const storedId = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (storedId) {
      const found = smbs.find((s: SMBData) => s.id === storedId)
      if (found) return found
    }

    // Fallback: Default to the first SMB in the list
    return smbs[0]
  }, [isAuthenticated, smbs, selectedId])

  const selectedSMBId = selectedSMB?.id || ''

  // Directly update state and session storage without useEffect race conditions
  const setSelectedSMBId = useCallback((smbId: string) => {
    setSelectedId(smbId)
    if (smbId) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, smbId)
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    }
  }, [])

  const loading = isAuthenticated ? isSmbsLoading : false
  const isError = isAuthenticated ? isSmbsError : false

  const refreshSMBs = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['smbsList'] })
  }, [queryClient])

  const value = useMemo(
    () => ({
      smbs,
      selectedSMB,
      selectedSMBId,
      loading,
      isError,
      setSelectedSMBId,
      refreshSMBs,
    }),
    [smbs, selectedSMB, selectedSMBId, loading, isError, setSelectedSMBId, refreshSMBs]
  )

  return <SMBContext.Provider value={value}>{children}</SMBContext.Provider>
}

export function useSMBContext(): SMBContextValue {
  const context = useContext(SMBContext)
  if (!context) {
    throw new Error('useSMBContext must be used within an SMBProvider')
  }
  return context
}
